import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { BoxIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import type { ExecutionImageView } from '@/lib/api/generated/model';
import { ListCard } from '@/shared/components/ListCard';
import { matchesName } from '@/shared/utils/matchesName';
import { useCanWork } from '@/features/workspaces/canWork';

import { isExecutionImageError } from '../errors';
import { useDeleteExecutionImage } from '../hooks/useDeleteExecutionImage';
import { useExecutionImages } from '../hooks/useExecutionImages';
import { useUpdateExecutionImage } from '../hooks/useUpdateExecutionImage';
import { AddExecutionImageSheet } from './AddExecutionImageSheet';
import { DeleteExecutionImageDialog } from './DeleteExecutionImageDialog';
import { EditExecutionImageLabelDialog } from './EditExecutionImageLabelDialog';
import { ExecutionImageList } from './ExecutionImageList';

const DESCRIPTION = 'The container images available for scheduled work.';

/**
 * Settings → Execution images (spec 228 US3): the workspace's image catalog on
 * the shared list card, read the way People is — the filter in the card's
 * toolbar, "N of M images", the loading, failed and empty states inside the
 * card, and "Add image" heading the tab where People's "Add member" does.
 *
 * The registry connections the images come from moved to Integrations →
 * Connections (spec 229); one line under the card says where. A failed load
 * still never renders as an empty list.
 */
export function ExecutionImagesPage({
  workspaceId,
  createRequested = false,
  onCreateDismissed,
}: {
  workspaceId: string;
  /** The address asked for the add sheet (spec 129, from the palette). */
  createRequested?: boolean;
  onCreateDismissed?: () => void;
}) {
  const canWork = useCanWork();
  const { images, isPending, loadFailed, refetch } =
    useExecutionImages(workspaceId);

  // The address's request opens the sheet from the first render, then is
  // spent so a reload does not open it again (the Connections rule).
  const [adding, setAdding] = useState(createRequested);
  useEffect(() => {
    if (createRequested) onCreateDismissed?.();
  }, [createRequested, onCreateDismissed]);
  const [editing, setEditing] = useState<ExecutionImageView | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ExecutionImageView | null>(
    null,
  );

  const update = useUpdateExecutionImage(workspaceId);
  const remove = useDeleteExecutionImage(workspaceId);

  const all = images ?? [];
  const [query, setQuery] = useState('');
  // Label or reference: "python" finds the image whether it is in the name
  // someone gave it or in the registry path.
  const shown = all.filter(
    (image) =>
      matchesName(image.label, query) ||
      matchesName(image.image_reference, query),
  );

  function handleUpdateImage(values: {
    label: string;
    imageReference: string;
    connectionId?: string;
  }) {
    if (!editing) return;
    const { id } = editing;
    update.mutate(
      {
        imageId: id,
        label: values.label,
        imageReference: values.imageReference,
        connectionId: values.connectionId,
      },
      {
        onSuccess: () => {
          setEditing(null);
          toast.success('Execution image updated.');
        },
      },
    );
  }

  function handleDelete() {
    if (!pendingDelete) return;
    const { label } = pendingDelete;
    remove.mutate(pendingDelete.id, {
      onSuccess: () => {
        setPendingDelete(null);
        toast.success(`Deleted ${label}.`);
      },
    });
  }

  function cancelDelete() {
    setPendingDelete(null);
    // A refusal belongs to the delete that drew it; the next one starts clean.
    remove.reset();
  }

  // A refusal keeps the dialog open with the service's reason in it
  // (spec 129 FR-005), rather than closing it and toasting.
  const deleteError =
    remove.error === null
      ? null
      : isExecutionImageError(remove.error)
        ? remove.error.message
        : `${pendingDelete?.label ?? 'It'} could not be deleted.`;

  const openAdd = () => {
    setAdding(true);
  };

  return (
    <div className="flex flex-col gap-4">
      {canWork ? (
        // A phone gives the tab's main action the full width (spec 276).
        <div className="flex justify-end max-md:[&>*]:w-full">
          <Button type="button" variant="outline" size="sm" onClick={openAdd}>
            <PlusIcon aria-hidden="true" />
            Add image
          </Button>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <ListCard
          title="Execution images"
          count={isPending || loadFailed ? undefined : all.length}
          headingId="execution-images-heading"
          description={DESCRIPTION}
          noun={['image', 'images']}
          state={
            isPending
              ? 'loading'
              : loadFailed
                ? 'failed'
                : all.length === 0
                  ? 'empty'
                  : 'idle'
          }
          failedText="The execution images could not be loaded."
          onRetry={() => void refetch()}
          skeletonRows={3}
          empty={{
            icon: BoxIcon,
            description:
              'No images yet. Add one to run scheduled work on something other than the default.',
            action: canWork ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openAdd}
              >
                Add image
              </Button>
            ) : undefined,
          }}
          search={{
            value: query,
            onChange: setQuery,
            placeholder: 'Filter by name or reference',
            label: 'Filter execution images',
          }}
          shown={shown.length}
          total={all.length}
          noResults={{
            description: `No image matches “${query.trim()}”.`,
            onClear: () => {
              setQuery('');
            },
          }}
        >
          <ExecutionImageList
            workspaceId={workspaceId}
            images={shown}
            onEdit={setEditing}
            onDelete={setPendingDelete}
          />
        </ListCard>
        <p className="text-muted-foreground text-xs">
          Images come from the registries connected under{' '}
          <Link
            to="/workspaces/$workspaceId/integrations/connections"
            params={{ workspaceId }}
            className="text-foreground focus-visible:ring-ring touch-pad rounded underline underline-offset-2 focus-visible:ring-3 focus-visible:outline-none"
          >
            Integrations → Connections
          </Link>
          .
        </p>
      </div>

      {adding ? (
        <AddExecutionImageSheet
          workspaceId={workspaceId}
          open
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}

      {editing ? (
        <EditExecutionImageLabelDialog
          key={editing.id}
          image={editing}
          open
          isPending={update.isPending}
          error={update.error}
          onSubmit={handleUpdateImage}
          onClearError={update.reset}
          onCancel={() => {
            setEditing(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <DeleteExecutionImageDialog
          image={pendingDelete}
          open
          isPending={remove.isPending}
          onConfirm={handleDelete}
          error={deleteError}
          onCancel={cancelDelete}
        />
      ) : null}
    </div>
  );
}
